<template>
  <div class="save-design-inline">
    <!-- 主保存按钮 -->
    <div class="save-form">
      <input type="email" v-model="email" placeholder="Enter your email" class="email-input" autocomplete="off" />
      <button class="save-btn" @click="saveDesign" :disabled="isSaving">
        {{ isSaving ? 'Saving...' : 'Save & Share Design' }}
      </button>
    </div>

    <!-- 成功后的分享选项 -->
    <div v-if="showShareOptions" class="share-options">
      <div class="share-success-message">
        ✓ Your design is saved successfully! Choose how you want to share it:
      </div>
      
      <!-- 按钮一：Master Link -->
      <div class="share-item">
        <div class="share-item-header">
          <span class="share-item-title">Master Link (Full Access)</span>
          <button class="copy-btn" @click="copyLink(masterLink)">Copy Link</button>
        </div>
        <div class="share-item-desc">
          Allows anyone to edit the 3D design, leave comments, and enter sizes. (Keep this for yourself or co-designers).
        </div>
      </div>
      
      <!-- 按钮二：Review Link -->
      <div class="share-item">
        <div class="share-item-header">
          <span class="share-item-title">Review Link (Comments & Roster)</span>
          <button class="copy-btn" @click="copyLink(reviewLink)">Copy Link</button>
        </div>
        <div class="share-item-desc">
          Allows your team to view the 3D model, leave comments, and enter sizes. Design is locked.
        </div>
      </div>
      
      <!-- 按钮三：Order Link -->
      <div class="share-item">
        <div class="share-item-header">
          <span class="share-item-title">Order Link (Roster Only)</span>
          <button class="copy-btn" @click="copyLink(orderLink)">Copy Link</button>
        </div>
        <div class="share-item-desc">
          Allows members to enter size info only. Design and comments are hidden/locked.
        </div>
      </div>
    </div>

    <!-- 保存状态提示 -->
    <div v-if="saveMessage" :class="['save-message', saveMessageType]">
      {{ saveMessage }}
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useDesignStore } from '@/stores/designStore'

export default {
  name: 'SaveButton',
  // 保留 props 以兼容旧代码（但不再用于数据收集）
  props: {
    currentGarment: { type: String, default: 'Short Sleeve' },
    garmentDesigns: { type: Object, default: () => ({}) },
    userColors: { type: Map, default: () => new Map() },
    partLayersMap: { type: Map, default: () => new Map() },
    textItems: { type: Array, default: () => [] },
    nextId: { type: Number, default: 1 },
    rosterPath: { type: String, default: '/roster' },
    reviewPath: { type: String, default: '/review' }
  },
  data() {
    return {
      email: '',
      isSaving: false,
      saveMessage: '',
      saveMessageType: '',
      showEmailStep: true,
      showLinkStep: false,
      designLink: '',
      showShareOptions: false,  // 是否显示分享选项
      masterLink: '',           // Master Link (完整权限)
      reviewLink: '',           // Review Link (评论+名单)
      orderLink: ''             // Order Link (仅名单)'
    }
  },
  computed: {
    ...mapState(useDesignStore, ['currentDesignId', 'currentGarment'])
  },
  methods: {
    ...mapActions(useDesignStore, ['saveCurrentGarmentData', 'exportAllGarmentsData']),
    
    updateUrlWithDesignId(id) {
      if (!id) return
      const url = new URL(window.location.href)
      if (url.searchParams.get('id') !== id) {
        url.searchParams.set('id', id)
        window.history.replaceState({}, '', url.toString())
      }
    },

    showSaveModal() {
      this.email = ''
      this.saveMessage = ''
      this.showEmailStep = true
      this.showLinkStep = false
      this.$refs.saveModal.style.display = 'flex'
      this.$nextTick(() => {
        const input = this.$refs.saveModal.querySelector('input')
        if (input) input.focus()
      })
    },
    
    closeSaveModal() {
      this.$refs.saveModal.style.display = 'none'
      this.email = ''
      this.saveMessage = ''
    },
    
    copyLink(link) {
      if (!link) return
      navigator.clipboard.writeText(link).then(() => {
        this.saveMessage = '✓ Link copied to clipboard!'
        this.saveMessageType = 'success'
        setTimeout(() => (this.saveMessage = ''), 2000)
      }).catch(() => {
        const textarea = document.createElement('textarea')
        textarea.value = link
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        this.saveMessage = '✓ Link copied to clipboard!'
        this.saveMessageType = 'success'
        setTimeout(() => (this.saveMessage = ''), 2000)
      })
    },

    collectAllGarmentsData() {
      const store = useDesignStore()
      return store.exportAllGarmentsData().all_garments
    },
    async saveDesign() {
      // 邮箱验证（可选）
      let emailToUse = null
      if (this.email && this.email.trim() !== '') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(this.email)) {
          this.saveMessage = 'Please enter a valid email address.'
          this.saveMessageType = 'error'
          setTimeout(() => (this.saveMessage = ''), 3000)
          return
        }
        emailToUse = this.email.trim()
      }
      
      this.isSaving = true
      this.saveMessage = ''
      
      const store = useDesignStore()
      
      // 保存当前款式数据
      store.saveCurrentGarmentData()
      
      // 收集所有款式数据
      const allGarmentsForSave = this.collectAllGarmentsData()
      
      const designData = {
        email: emailToUse,
        current_garment: store.currentGarment || 'Short Sleeve',
        all_garments: allGarmentsForSave
        // roster 不在这里发送 — roster 由 RosterManager 单独通过 save-roster.php 管理
        // 若在此发送 store.rosterList（可能为空数组），会覆盖服务器上已保存的 roster 数据
      }
      
      if (store.currentDesignId) {
        designData.design_id = store.currentDesignId
      }
      
      try {
        const response = await fetch('/save-design.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(designData)
        })
        
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        
        const result = await response.json()
        if (!result.success) throw new Error(result.message || 'Save failed')
        
        const newId = result.design_id
        if (!store.currentDesignId && newId) {
          store.currentDesignId = newId
          this.updateUrlWithDesignId(newId)
        }
        
        // 生成三种链接（改为指向不同的HTML文件）
        const baseUrl = window.location.origin
        this.masterLink = `${baseUrl}/index.html?id=${store.currentDesignId}`
        this.reviewLink = `${baseUrl}${this.reviewPath}?id=${store.currentDesignId}`
        this.orderLink  = `${baseUrl}${this.rosterPath}?id=${store.currentDesignId}`
        
        // 显示分享选项
        this.showShareOptions = true
        this.saveMessage = '✓ Design saved successfully!'
        this.saveMessageType = 'success'
        
        // 发送邮件（如果有邮箱）
        if (emailToUse) {
          try {
            await fetch('/send-email.php', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                email: emailToUse, 
                design_link: this.masterLink,
                review_link: this.reviewLink,
                order_link: this.orderLink
              })
            })
          } catch (e) {
            console.error('Email send error:', e)
          }
        }
        
        this.$emit('design-saved', { 
          designId: store.currentDesignId, 
          link: this.masterLink,
          reviewLink: this.reviewLink,
          orderLink: this.orderLink
        })
        
      } catch (error) {
        console.error('Save error:', error)
        this.saveMessage = 'Failed to save: ' + (error.message || 'Unknown error')
        this.saveMessageType = 'error'
      } finally {
        this.isSaving = false
        setTimeout(() => {
          if (this.saveMessage && this.saveMessageType !== 'error') {
            this.saveMessage = ''
          }
        }, 5000)
      }
    },
    
    // 将 store 中的 all_garments 转换为后端期望的格式（colors 为普通对象，logos 数组）
    convertAllGarmentsForBackend(allGarmentsData) {
      const converted = {}
      for (const [garment, data] of Object.entries(allGarmentsData)) {
        converted[garment] = {
          colors: {},
          texts: [],
          logos: []
        }
        
        // 1. 转换颜色（支持 Map 和普通对象）
        if (data.colors) {
          if (data.colors instanceof Map) {
            data.colors.forEach((colorValue, part) => {
              const hex = typeof colorValue === 'number'
                ? '#' + colorValue.toString(16).padStart(6, '0')
                : colorValue
              converted[garment].colors[part] = hex
            })
          } else {
            converted[garment].colors = data.colors
          }
        }
        
        // 2. 转换文字（从 layers 或直接 data.texts）
        const texts = []
        if (data.texts && Array.isArray(data.texts)) {
          data.texts.forEach(text => {
            texts.push({
              id: text.id,
              text: text.text,
              font: text.font,
              color: text.color,
              part: text.part,
              scale: text.scale || 0.8,
              offsetX: text.offsetX || 0,
              offsetY: text.offsetY || 0,
              fontSize: text.fontSize || 80,
              rotation: text.rotation || 0
            })
          })
        } else if (data.layers) {
          // 从 layers 中提取文字
          if (data.layers instanceof Map) {
            for (const [part, layers] of data.layers.entries()) {
              layers.forEach(layer => {
                if (layer.isText) {
                  texts.push({
                    id: layer.id,
                    text: layer.textContent,
                    font: layer.textFont,
                    color: layer.textColor,
                    part: part,
                    scale: layer.scale || 0.8,
                    offsetX: layer.offsetX || 0,
                    offsetY: layer.offsetY || 0,
                    fontSize: layer.fontSize || 80,
                    rotation: layer.rotation || 0
                  })
                }
              })
            }
          }
        }
        converted[garment].texts = texts
        
        // 3. 转换 Logo（图片）
        const logos = []
        if (data.layers) {
          if (data.layers instanceof Map) {
            for (const [part, layers] of data.layers.entries()) {
              layers.forEach(layer => {
                if (!layer.isText && layer.url) {
                  logos.push({
                    id: layer.id,
                    part: part,
                    url: layer.url,
                    scale: layer.scale || 1,
                    offsetX: layer.offsetX || 0,
                    offsetY: layer.offsetY || 0,
                    rotation: layer.rotation || 0
                  })
                }
              })
            }
          }
        } else if (data.logos && Array.isArray(data.logos)) {
          data.logos.forEach(logo => {
            logos.push(logo)
          })
        }
        converted[garment].logos = logos
      }
      return converted
    },
    

   
  }
}
</script>

<style scoped>
/* 样式保持不变（与原来完全相同） */
.save-design-inline {
  background: transparent;
  border-radius: 0;
  padding: 0;
  margin: 0 0 0.5rem 0;
  border: none;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #072a47;
  padding-left: 4px;
  margin-bottom: 0;
}

.save-form {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.email-input {
  flex: 2;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.85rem;
  background-color: #f0fff0;
}

.save-btn {
  background: #10b981;
  border: none;
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  transition: 0.2s;
}

.save-btn:hover {
  background: #059669;
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.save-message {
  font-size: 0.75rem;
  margin-left: 8px;
  white-space: nowrap;
}

.save-message.success {
  color: #10b981;
}
.save-message.error {
  color: #ef4444;
}
.save-message.warning {
  color: #f59e0b;
}

.save-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  z-index: 20000;
  display: none;
  justify-content: center;
  align-items: center;
}

.save-modal {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-radius: 24px;
  padding: 2rem 1.8rem 1.8rem;
  width: 90%;
  max-width: 400px;
  text-align: center;
  border: 1px solid rgba(59, 130, 246, 0.5);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  position: relative;
}

.save-modal h3 {
  color: #ffffff;
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.save-modal .modal-subtitle {
  color: #94a3b8;
  font-size: 0.8rem;
  margin-bottom: 1.5rem;
}

.save-modal input {
  width: 100%;
  padding: 12px 16px;
  background: #334155;
  border: 1px solid #475569;
  border-radius: 16px;
  font-size: 1rem;
  color: #ffffff;
  outline: none;
  box-sizing: border-box;
}

.save-modal input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
}

.save-modal-buttons {
  display: flex;
  gap: 12px;
  margin-top: 1.5rem;
}

.save-modal-btn {
  flex: 1;
  padding: 10px 0;
  border-radius: 40px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  border: none;
  font-family: inherit;
}

.save-modal-btn.confirm {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
}

.save-modal-btn.confirm:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.4);
}

.save-modal-btn.cancel {
  background: #334155;
  color: #cbd5e1;
}

.save-modal-btn.cancel:hover {
  background: #475569;
}

.close-modal-btn {
  position: absolute;
  top: 14px;
  right: 18px;
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 1.3rem;
  cursor: pointer;
  transition: color 0.2s;
}

.close-modal-btn:hover {
  color: #ef4444;
}

.design-link-display {
  background: #334155;
  padding: 10px;
  border-radius: 12px;
  word-break: break-all;
  margin: 15px 0;
  cursor: pointer;
  color: #60a5fa;
  font-size: 0.85rem;
}

/* 分享选项区域 */
.share-options {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
}

.share-success-message {
  font-size: 0.85rem;
  color: #10b981;
  margin-bottom: 1rem;
  text-align: center;
}

.share-item {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
  border: 1px solid #e0e0e0;
}

.share-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
  gap: 8px;
}

.share-item-title {
  font-weight: 600;
  font-size: 0.85rem;
  color: #333;
}

.copy-btn {
  background: #3b82f6;
  border: none;
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 0.7rem;
  cursor: pointer;
  transition: background 0.2s;
}

.copy-btn:hover {
  background: #2563eb;
}

.share-item-desc {
  font-size: 0.7rem;
  color: #666;
  line-height: 1.4;
}

@media (max-width: 850px) {
  .save-form {
    flex-direction: column;
    align-items: stretch;
  }
  .save-message {
    white-space: normal;
    margin-left: 0;
    margin-top: 4px;
  }
}
</style>